import { Link as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  useMediaQuery,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse
} from '@material-ui/core';
import { experimentalStyled } from '@material-ui/core/styles';
import MenuIcon from '../../icons/Menu';
import Logo from '../Logo';
import useAuth from '../../hooks/useAuth';
import { LanguageSelector } from '../../react-auto-google-translate/lib/main.ts';
import { googleTranslateLanguages } from '../../utils/languages';
import { useState, useEffect } from 'react';
import ChevronDown from '../../icons/ChevronDown';
import SignOutIcon from '../../icons/SignOut';

const NavbarRoot = experimentalStyled('header')(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? 'white' : theme.palette.background.paper,
  borderBottom: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : `1px solid ${theme.palette.divider}`,
  color: theme.palette.primary.contrastText,
  width: '100%',
  padding: '0 1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '64px',
  position: 'sticky',
  top: 0,
  zIndex: 1000
}));

// Navigation items - similar to those in SideMenuSidebar
const navItems = [
  { title: 'Conhecer-me',
    submenu: [
      { title: 'Avaliação de Competências Transversais', path: '/conhecer-me/competencias-pessoais' },
      { title: 'Avaliação de Competências Digitais', path: '/conhecer-me/competencias-digitais' },
      { title: 'Avaliação de Competências Profissionais', path: '/conhecer-me/competencia-profissional' },
      { title: 'Serei Empreendedor?', path: '/conhecer-me/serei-empreendedor' },
      { title: 'Valores de Vida', path: '/conhecer-me/valores-vida' },
      { title: 'Transições de Carreira', path: '/conhecer-me/transicoes-carreira' },
      { title: 'Orientação de Carreira', path: '/conhecer-me/orientacao-carreira' },
    ]
  },
  {
    title: 'Explorar',
    submenu: [
      { title: 'Profissões', path: '/explorar/profissoes' },
      { title: 'Formações', path: '/explorar/formacoes' },
      { title: 'Profissões Primeira Infância', path: '/profissoes-primeira-infancia' },
      { title: 'Quero Ser Professor', path: '/professores' }
    ]
  },
  {
    title: 'Carreira',
    submenu: [
      { title: 'Simulador de Carreira', path: '/simulador-de-carreira' },
      { title: 'Preparar-me', path: '/preparar-me/mercado-trabalho' },
      { title: 'Casos de Sucesso', path: '/preparar-me/casos-sucesso' },
    ]
  },
  {
    title: 'Emprego',
    submenu: [
      { title: 'Oportunidades de Emprego', path: '/oportunidades-emprego' },
      { title: 'Mapa de Emprego', path: '/mapa-emprego/tendencias' },
      { title: 'O Futuro da Minha Profissão', path: '/o-futuro-da-minha-profissao' },
      { title: 'Tendências do Mercado de Trabalho', path: 'https://radar.mymentor.pt', external: true },
      { title: 'Descobre o teu Futuro Emprego', path: '/quiz' },
    ]
  },
];

const Navbar = (props) => {
  const { onSidebarMobileOpen, ...other } = props;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const auth = useAuth();
  const { logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenuTitle, setOpenMenuTitle] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState('');
  const location = useLocation();

  const handleMenuOpen = (event, title) => {
    setAnchorEl(event.currentTarget);
    setOpenMenuTitle(title);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenMenuTitle('');
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSubmenuToggle = (title) => {
    if (expandedSubmenu === title) {
      setExpandedSubmenu('');
    } else {
      setExpandedSubmenu(title);
    }
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    handleMobileMenuClose();
    logout();
  };

  // Close mobile menu when route changes
  useEffect(() => {
    handleMobileMenuClose();
  }, [location.pathname]);

  // Mobile menu content
  const mobileMenuContent = (
    <Box sx={{ width: 280, p: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
        <Box
          component={RouterLink}
          to="/"
          onClick={handleMobileMenuClose}
        >
          <Logo sx={{ height: 30, maxWidth: 150 }} />
        </Box>
      </Box>

      <LanguageSelector
        className="mobile-language-selector"
        onlySpecificLanguages={googleTranslateLanguages}
        componentScale={0.78}
      />

      <List sx={{ mt: 2 }}>
        {navItems.map((item) => (
          <div key={item.title}>
            {item.submenu ? (
              <>
                <ListItem
                  button
                  onClick={() => handleSubmenuToggle(item.title)}
                >
                  <ListItemText primary={item.title} />
                  <ChevronDown
                    sx={{
                      transform: expandedSubmenu === item.title ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }}
                  />
                </ListItem>
                <Collapse
                  in={expandedSubmenu === item.title}
                  timeout="auto"
                  unmountOnExit
                >
                  <List
                    component="div"
                    disablePadding
                  >
                    {item.submenu.map((subItem) => (
                      <ListItem
                        button
                        key={subItem.title}
                        component={subItem.external ? 'a' : RouterLink}
                        to={!subItem.external ? subItem.path : undefined}
                        href={subItem.external ? subItem.path : undefined}
                        onClick={handleMobileMenuClose}
                        target={subItem.external ? '_blank' : undefined}
                        sx={{ pl: 4 }}
                      >
                        <ListItemText primary={subItem.title} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem
                button
                component={RouterLink}
                to={item.path}
                onClick={handleMobileMenuClose}
              >
                <ListItemText primary={item.title} />
              </ListItem>
            )}
          </div>
        ))}
      </List>

      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!auth.isAuthenticated ? (
          <>
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleMobileMenuClose}
            >
              Login
            </Button>
            <Button
              component={RouterLink}
              to="/registar"
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleMobileMenuClose}
            >
              Registar
            </Button>
          </>
        ) : (
          <>
            <Button
              component={RouterLink}
              to="/perfil"
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleMobileMenuClose}
            >
              Minha Conta
            </Button>
            <Button
              color="secondary"
              variant="contained"
              fullWidth
              onClick={handleLogout}
              startIcon={<SignOutIcon />}
            >
              Terminar Sessão
            </Button>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <NavbarRoot {...other}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {isMobile && (
          <IconButton
            onClick={handleMobileMenuToggle}
            sx={{ color: '#C64DEC', mr: 1 }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          component={RouterLink}
          to="/"
        >
          <Logo
            sx={{
              height: 30,
              maxWidth: 150
            }}
          />
        </Box>
      </Box>

      {/* Desktop Navigation */}
      {!isMobile && (
        <Box
          component="nav"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Box
            sx={{
              marginRight: '20px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1
            }}
          >
            {navItems.map((item) => (
              <Box
                key={item.title}
                sx={{ position: 'relative' }}
              >
                {item.submenu ? (
                  <>
                    <Button
                      color="primary"
                      onClick={(e) => handleMenuOpen(e, item.title)}
                      sx={{ fontSize: '12px' }}
                      endIcon={<ChevronDown />}
                    >
                      {item.title}
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenuTitle === item.title}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                    >
                      {item.submenu.map((subItem) => (
                        subItem.external ? (
                          <MenuItem
                            key={subItem.title}
                            component="a"
                            href={subItem.path}
                            onClick={handleMenuClose}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {subItem.title}
                          </MenuItem>
                        ) : (
                          <MenuItem
                            key={subItem.title}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={handleMenuClose}
                          >
                            {subItem.title}
                          </MenuItem>
                        )
                      ))}
                    </Menu>
                  </>
                ) : (
                  <Button
                    color="primary"
                    component={RouterLink}
                    sx={{ fontSize: '12px' }}
                    to={item.path}
                  >
                    {item.title}
                  </Button>
                )}
              </Box>
            ))}
          </Box>

          <LanguageSelector
            className="desktop-language-selector"
            onlySpecificLanguages={googleTranslateLanguages}
            componentScale={0.9}
          />

          {!auth.isAuthenticated ? (
            <>
              <Button
                component={RouterLink}
                to="/login"
                sx={{ ml: 2 }}
                variant="outlined"
                color="primary"
                size="small"
              >
                Login
              </Button>
              <Button
                component={RouterLink}
                to="/registar"
                sx={{ ml: 2, fontSize: '12px' }}
                variant="contained"
                color="primary"
                size="small"
              >
                Registar
              </Button>
            </>
          ) : (
            <Button
              component={RouterLink}
              to="/perfil"
              sx={{ ml: 2, fontSize: '12px' }}
              variant="outlined"
              color="primary"
            >
              Minha Conta
            </Button>
          )}
        </Box>
      )}

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        variant="temporary"
        PaperProps={{
          sx: { backgroundColor: 'background.paper' }
        }}
      >
        {mobileMenuContent}
      </Drawer>
    </NavbarRoot>
  );
};

Navbar.propTypes = {
  onSidebarMobileOpen: PropTypes.func,
};

export default Navbar;
